import {Drawer, Indicator} from '@mantine/core';
import type {FC} from 'react';
import {FaExternalLinkAlt} from 'react-icons/fa';
import {MdAccountCircle, MdOutlineFiberNew} from 'react-icons/md';
import {
  CAREER_ADVISER_INTERVIEW_TRAINING,
  COLUMNS,
  ENTRY_MANAGEMENTS_PROGRESS,
} from '../../../../config/constants/routes';
import {isReleasedCareerAdviser} from '../../../../config/unReleasePage';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {INTERVIEW_TRAINING_FEATURE_NAME} from '../../../../features/careerAdviser/constants/metaData';
import {ENTRY_MANAGEMENT_FEATURE_NAME} from '../../../../features/entryManagements/constants/metaData';
import {useEntryManagementReminders} from '../../../../features/entryManagements/usecase/useEntryManagementReminders';
import {CommonLink} from '../../CommonLink';
import {overlayConfig} from '../../modalConfig';
import {HeaderMenu} from '../Menu';

type Props = {
  opened: boolean;
  onClose: () => void;
  scoutsCount: number;
  interestsCount: number;
  todayDeadlineRecruitmentCount: number;
};

type BadgeType = 'deadline' | 'scouts' | 'entryManagement';

const spMenuItems: Array<{
  name: string;
  url: string;
  isNew?: boolean;
  isUpdate?: boolean;
  badgeType?: BadgeType;
}> = [
  {
    name: 'ホーム',
    url: '/',
  },
  {
    name: '募集を探す',
    url: '/recruiting_info',
    badgeType: 'deadline',
  },
  {
    name: '企業を探す',
    url: '/company',
  },
  {
    name: 'ES・体験記',
    url: '/reports',
  },
  {
    name: 'AI選考対策',
    url: '/report_analytics/search',
  },
  ...(isReleasedCareerAdviser()
    ? [
        {
          name: INTERVIEW_TRAINING_FEATURE_NAME,
          url: CAREER_ADVISER_INTERVIEW_TRAINING,
          isNew: true,
        },
      ]
    : []),
  {
    name: ENTRY_MANAGEMENT_FEATURE_NAME,
    url: ENTRY_MANAGEMENTS_PROGRESS,
    isNew: true,
    badgeType: 'entryManagement' as BadgeType,
  },
  {
    name: 'コラム',
    url: COLUMNS,
  },
  {
    name: 'エンジニア',
    url: '/engineer',
  },
  {
    name: 'スカウト',
    url: '/scouts',
    badgeType: 'scouts',
  },
];

export const SPDrawer: FC<Props> = ({
  opened,
  onClose: close,
  scoutsCount,
  interestsCount,
  todayDeadlineRecruitmentCount,
}) => {
  const {user, signOut, status} = useAuth();
  const isSignedIn = status === 'authenticated';

  const {totalCount: entryManagementRemindersCount} =
    useEntryManagementReminders();

  const badgeCounts: Record<BadgeType, number> = {
    deadline: todayDeadlineRecruitmentCount,
    scouts: scoutsCount,
    entryManagement: entryManagementRemindersCount,
  };

  return (
    <Drawer
      position="left"
      withCloseButton={false}
      opened={opened}
      onClose={() => {
        close();
      }}
      padding={0}
      size="70%"
      className="top-52"
      {...overlayConfig}
    >
      <div className="h-full p-16 text-black-1 bg-background-2">
        <div className="overflow-hidden rounded-12 bg-white-1 p-12">
          {user && (
            <div className="flex gap-8 px-12 pb-4 pt-12">
              <MdAccountCircle size={24} />
              <div className="font-bold">{user.familyName}さん</div>
            </div>
          )}
          <HeaderMenu
            scoutsCount={scoutsCount}
            interestsCount={interestsCount}
            onLogout={signOut}
            className="w-full bg-white-1"
            onItemClick={close}
            showMyPage={isSignedIn}
          />
        </div>
        <ul className="mt-12 flex flex-col divide-y divide-line">
          {spMenuItems.map((item) => {
            const badgeCount = item.badgeType ? badgeCounts[item.badgeType] : 0;
            return (
              <CommonLink
                key={item.name}
                href={item.url}
                onClick={close}
                className="flex items-center p-12 font-bold"
              >
                {item.isNew && <NewIcon />}
                {item.isUpdate && <UpdateIcon />}
                <Indicator
                  color="red"
                  position="middle-end"
                  offset={-16}
                  size={24}
                  label={`${badgeCount}`}
                  disabled={!badgeCount}
                >
                  <span className="ml-8">{item.name}</span>
                </Indicator>
              </CommonLink>
            );
          })}
          {!isSignedIn && (
            <li className="p-12">
              <CommonLink
                href="https://biz.gaishishukatsu.com/"
                className="flex items-center font-bold leading"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="ml-8">採用担当者様はこちら</span>
                <span>
                  <FaExternalLinkAlt />
                </span>
              </CommonLink>
            </li>
          )}
        </ul>
      </div>
    </Drawer>
  );
};

const NewIcon: FC = () => {
  return <MdOutlineFiberNew size={36} className="text-accent-1" />;
};

const UpdateIcon: FC = () => {
  return (
    <div className="font-bold rounded-12 text-center text-[8px] text-accent-1 h-12 mr-4 w-32">
      UPDATE
    </div>
  );
};
